'use client'

import React from 'react'
import { Mail, Send, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, RefreshCw, Paperclip } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { CopyChip } from '../ProcessedEntry/DomainEntry/sharedComponents'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { EmailPillInput } from '../EmailPillInput'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { CommunicationMockup, type Communication } from '@shared/postui'
import { parseDbTimestamp } from '@/lib/dates'

// ─── Shared type ──────────────────────────────────────────────────────────────

export type SibsEntry = {
  user_entry_id: string | null
  created_at: string
  email_sent: boolean
  entry_type: string | null
  entity_reference_id: string | null
  entity_reference_entity: string | null
  entity_reference_reference: string | null
  // The canonical domain.id this entry's linked domain resolves to — see
  // get_sibs_fraud_entries. Null wherever no domain has been linked, which
  // is most multibanco/MULTIBANCO-type entries (domain linkage is mainly an
  // ENTITY_REFERENCE/CTT-style takedown concept, not a bank-fraud one).
  domain_id?: string | null
  beneficiario: string | null
  // "Pediste esta mensagem?" from the multibanco validator (Hono reads it off
  // original_payload.ab_tests): true = the user did not ask for it, null = never asked.
  unsolicited?: boolean | null
  sender: string | null
  message_content: string
  scam_claims: any
  platform_namelong: string | null
  platform_text: string | null
  post_type: string | null
  source_type: string | null
  source: string | null
  message_type_id: number | string | null
  message_type_text: string | null
  raw_free_input: string | null
  r2_key: string | null
  raw_entry_original_payload?: Record<string, unknown> | null
  raw_entry_parsed_payload?: Record<string, unknown> | null
  pipeline_steps: { step: string; json_info: any; created_at: string }[]
  // Raw-entry-editing fields, populated whenever this entry has a backing
  // raw_entries row to edit — multibanco-origin, whether still raw or since
  // accepted/processed (see raw_entry_id) — null for genuinely native
  // MBWAY/CALLS-sourced entries with no raw_entries row at all. Gate the
  // editing panel on raw_entry_id being non-null, not on source_type ===
  // 'raw_entry': once accepted, source_type moves to 'processed_entry' (or
  // 'brand_campaign'), but the entry may still be raw-entry-editable.
  revision_status?: string | null
  platform_id?: number | null
  message_subject?: string | null
  call_type_id?: number | null
  call_type_text?: string | null
  is_scam?: boolean | null
  scam_value?: number | null
  // The id the Raw Entry -> Processed Entry Management panel's Accept/
  // Reject/Assign Fields actions need (rawEntryId in the /accept, /reject,
  // /fields payloads) — distinct from `id` below because the two diverge
  // once this entry's row has been deduped onto its processed-entry
  // representation (get_sibs_fraud_entries' branch_multibanco_or_processed):
  // `id` there is the processed entry, not the raw one.
  raw_entry_id?: string | null
  // Who submitted it: user_entries.user_id and that user's email (when the
  // id has a users row) -- null for anonymous submissions and calls.
  user_id?: string | null
  user_email?: string | null
  // raw_entries.browser_id: the submitting browser's persistent id.
  browser_id?: string | null
  // What Cloudflare reported about the submitting request (raw_entries'
  // *_at_create_time, from request.cf at ingest) -- null without a raw entry.
  country_at_create_time?: string | null
  city_at_create_time?: string | null
  asn_at_create_time?: string | number | null
  // Where the raw entry's request came from — Cloudflare's geo, stored on
  // raw_entries as *_at_create_time; same names unified_entries_flat /
  // raw_entries_flat expose them under. Null for entries with no raw row.
  raw_entry_city_at_create_time?: string | null
  raw_entry_region_at_create_time?: string | null
  raw_entry_country_at_create_time?: string | null
  raw_entry_continent_at_create_time?: string | null
  id: string
}

// ─── Shared error/retry banner ────────────────────────────────────────────────
// One unified look for "this fetch failed, here's a button to try again"
// across every chart/widget on the SIBS dashboard (dashboard aggregates,
// the volume chart, dispatch logs, and every per-entry log list below) —
// several of these used to swallow failures silently (a timed-out fetch
// just looked like "no data"/"no logs" instead of an actual error) or show
// a bare line of red text with no way to retry short of the entry's own
// unmarked "refresh" link.
export function RetryBanner({
  message,
  onRetry,
  retrying = false,
  isDark,
}: {
  message: string
  onRetry: () => void
  retrying?: boolean
  isDark: boolean
}) {
  return (
    <div className={`flex items-center gap-2 rounded border px-3 py-2 font-mono text-[11px] border-destructive/40 bg-danger-surface text-destructive`}>
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1">{message}</span>
      <button
        onClick={onRetry}
        disabled={retrying}
        className={`flex shrink-0 items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 border-destructive hover:bg-danger-surface`}
      >
        <RefreshCw className={`h-3 w-3 ${retrying ? 'animate-spin' : ''}`} />
        Retry
      </button>
    </div>
  )
}

// These per-entry log lists (email log, case report log, entity-reference
// emails) hit the same logs table that's been timing out under load almost
// every time it's queried. Surfacing that with a RetryBanner on every single
// failure would mean the operator sees an error flash on nearly every entry
// they open. Instead: retry silently a few times first (a random 1-5s delay,
// so several open widgets don't all hammer the endpoint again at the exact
// same instant) — only falling back to the visible RetryBanner once the
// silent budget is exhausted, so a persistent failure still doesn't go
// unnoticed forever.
const LOGS_SILENT_MAX_AUTO_RETRIES = 5
const logsSilentRetryDelayMs = () => 1000 + Math.random() * 4000

// ─── Internal helpers ─────────────────────────────────────────────────────────

function claimCount(v: any): number {
  if (v == null) return 0
  if (typeof v === 'number') return v
  if (Array.isArray(v)) return v.length
  if (typeof v === 'object') return Object.keys(v).length
  const n = parseInt(String(v), 10)
  return isNaN(n) ? 0 : n
}

// Generated client-side (rather than by the backend at send time) so the
// compose UI can preview the exact subject before sending. This becomes the
// email_logs.id primary key on the backend — the full UUID (not a truncated
// hash) so the "[REF-<uuid>]" subject tag IS the row's real id, and an inbound
// reply can be matched back with a plain primary-key lookup (see
// routes/cloudflareWorkers/email.ts).
function newLogId() {
  return crypto.randomUUID()
}

function fmtDateShort(iso: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

function sibsDispatchSubjectScope(entry: SibsEntry) {
  if (entry.entry_type === 'MBWAY' && entry.sender?.trim()) {
    return `MBWAY · ${entry.sender.trim()}`
  }

  if (entry.entry_type === 'ENTITY_REFERENCE') {
    const entityReference = [entry.entity_reference_entity, entry.entity_reference_reference]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' / ')

    return entityReference ? `ENTITY_REFERENCE · ${entityReference}` : 'ENTITY_REFERENCE'
  }

  return entry.entry_type ?? 'REPORT'
}

// ─── MockupToggleCard ─────────────────────────────────────────────────────────
// Same include/exclude-by-default gallery-card pattern used for the "Report"
// mockup thumbnail in the CTT brand campaign builder (CampaignForm.tsx).

const MOCKUP_THUMB_W = 88
const MOCKUP_THUMB_H = 136

function MockupToggleCard({
  communication,
  included,
  onToggle,
  theme,
}: {
  communication: Communication
  included: boolean
  onToggle: () => void
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'

  return (
    <div
      onClick={onToggle}
      role="button"
      className={`relative shrink-0 cursor-pointer select-none overflow-hidden border-2 transition-all ${
        included ? 'border-signal' : 'border-transparent opacity-70 hover:opacity-100 hover:border-signal/50'
      }`}
      style={{ width: MOCKUP_THUMB_W }}
    >
      <div className={`relative overflow-hidden bg-secondary`} style={{ height: MOCKUP_THUMB_H }}>
        <div style={{ transform: `scale(${MOCKUP_THUMB_W / 320})`, transformOrigin: 'top left', width: 320 }}>
          <CommunicationMockup communication={communication} theme={theme} />
        </div>
        {/* Explicit state badge either way — dimming alone left "not
            included" looking unfinished/loading rather than a deliberate
            unselected state. */}
        {included ? (
          <div className="absolute top-1 left-1 flex h-4 w-4 items-center justify-center rounded-sm bg-signal shadow">
            <span className="text-[9px] font-bold leading-none text-white">✓</span>
          </div>
        ) : (
          <div className={`absolute top-1 left-1 flex h-4 w-4 items-center justify-center rounded-sm border shadow ${isDark ? 'border-slate-500 bg-card' : 'border-border bg-card'}`}>
            <span className={`text-[9px] font-bold leading-none text-subtle`}>✕</span>
          </div>
        )}
      </div>
      <div className={`border-t px-1 py-1 text-center font-mono text-[7px] truncate ${c.line} ${included ? 'text-signal' : c.faint}`}>
        Message mockup
      </div>
    </div>
  )
}

// ─── SibsEntryDataWidget ──────────────────────────────────────────────────────

function DataRow({ label, value, c, mono = true }: {
  label: string
  value: React.ReactNode
  c: ReturnType<typeof watchClasses>
  mono?: boolean
}) {
  return (
    <tr className={`border-b last:border-0 ${c.line}`}>
      <td className={`px-3 py-1.5 align-top font-mono text-[10px] uppercase tracking-widest ${c.faint}`} style={{ width: '40%' }}>
        {label}
      </td>
      {/* break-all, not break-words: the values that matter here are ids,
          references and enum-ish tokens with no natural break points, and
          they must wrap inside the column rather than push the table wider
          than the panel (see table-fixed below). */}
      <td className={`px-3 py-1.5 align-top break-all ${mono ? 'font-mono text-[12px]' : 'text-[13px]'} ${c.text}`}>
        {value ?? <span className={c.faint}>—</span>}
      </td>
    </tr>
  )
}

// Before 2026-08-03 the backend kept `browserId` inside original_payload;
// since then it lives only in raw_entries.browser_id (entry.browser_id). The
// payload can arrive as a JSON string (some row shapes keep the text).
function payloadBrowserId(payload: unknown): string | null {
  let p = payload
  if (typeof p === 'string') { try { p = JSON.parse(p) } catch { return null } }
  const v = p && typeof p === 'object' ? (p as Record<string, unknown>).browserId : null
  return typeof v === 'string' && v ? v : null
}

// "Lisbon, Lisbon, PT · EU" — a city equal to its region is not repeated.
function entryLocation(entry: SibsEntry): string | null {
  const city = entry.raw_entry_city_at_create_time?.trim() || null
  const region = entry.raw_entry_region_at_create_time?.trim() || null
  const country = entry.raw_entry_country_at_create_time?.trim() || null
  const continent = entry.raw_entry_continent_at_create_time?.trim() || null
  const place = [city, region && region !== city ? region : null, country].filter(Boolean).join(', ')
  if (!place) return continent
  return continent ? `${place} · ${continent}` : place
}

export function SibsEntryDataWidget({ entry, theme }: { entry: SibsEntry; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  // The submitting browser (the consumer apps send one with every post): the
  // stand-in for a user id on anonymous submissions, which is most of them.
  const browserId = entry.browser_id ?? payloadBrowserId(entry.raw_entry_original_payload)

  return (
    <div className={`overflow-hidden rounded border ${c.line}`}>
      {/* table-fixed: an auto-layout table sizes its columns to the widest
          cell, so in a narrow panel it silently grew past the panel's edge
          and got clipped (values cut mid-word while the panel below sat
          empty). Fixed layout keeps the 40/60 split at any width and lets
          the rows wrap down into the space the panel actually has. */}
      <table className="w-full table-fixed border-collapse">
        <tbody className={'bg-card'}>
          <DataRow label="User Entry ID" value={entry.user_entry_id ? <CopyChip value={entry.user_entry_id} display={entry.user_entry_id.slice(0, 8)} /> : null} c={c} />
          <DataRow label="ID"            value={<CopyChip value={entry.id} display={entry.id.slice(0, 8)} />} c={c} />
          <DataRow label="User ID"       value={entry.user_id ? <CopyChip value={entry.user_id} display={entry.user_id.length > 12 ? entry.user_id.slice(0, 8) : entry.user_id} /> : null} c={c} />
          <DataRow label="User Email"    value={entry.user_email ? <CopyChip value={entry.user_email} display={entry.user_email} /> : null} c={c} />
          <DataRow label="Browser ID"    value={browserId ? <CopyChip value={browserId} display={browserId.length > 12 ? browserId.slice(0, 8) : browserId} /> : null} c={c} />
          <DataRow label="Country (CF)"  value={entry.country_at_create_time} c={c} />
          <DataRow label="City (CF)"     value={entry.city_at_create_time} c={c} />
          <DataRow label="ASN (CF)"      value={entry.asn_at_create_time != null ? String(entry.asn_at_create_time) : null} c={c} />
          <DataRow label="Created At"    value={fmtDateShort(entry.created_at)} c={c} />
          <DataRow label="Location"      value={entryLocation(entry)} c={c} mono={false} />
          <DataRow label="Entry Type"    value={entry.entry_type} c={c} />
          <DataRow label="Post Type"     value={entry.post_type} c={c} />
          <DataRow label="Stage"         value={entry.source_type} c={c} />
          <DataRow label="Source"        value={entry.source} c={c} />
          <DataRow label="Sender"        value={entry.sender} c={c} />
          <DataRow label="Platform"      value={entry.platform_namelong} c={c} />
          <DataRow label="Platform Text" value={entry.platform_text} c={c} />
          <DataRow label="Entity Ref ID" value={entry.entity_reference_id ? <CopyChip value={entry.entity_reference_id} display={entry.entity_reference_id.slice(0, 8)} /> : null} c={c} />
          <DataRow label="Domain ID"     value={entry.domain_id ? <CopyChip value={entry.domain_id} display={entry.domain_id.slice(0, 8)} /> : null} c={c} />
          <DataRow label="Entity"        value={entry.entity_reference_entity} c={c} />
          <DataRow label="Reference"     value={entry.entity_reference_reference ? <CopyChip value={entry.entity_reference_reference} display={entry.entity_reference_reference} /> : null} c={c} />
          <DataRow label="Beneficiário"  value={entry.beneficiario} c={c} />
          <DataRow label="Unsolicited"   value={unsolicitedLabel(entry)} c={c} />
          <DataRow label="Scam Claims"   value={entry.scam_claims != null ? String(claimCount(entry.scam_claims)) : null} c={c} />
          <DataRow label="Email Sent"    value={entry.email_sent ? '✓ yes' : '✗ no'} c={c} />
          <DataRow label="R2 Key"        value={entry.r2_key} c={c} />
          {entry.pipeline_steps?.length > 0 && (
            <DataRow label="Pipeline" value={entry.pipeline_steps.map(s => s.step).join(' → ')} c={c} mono={false} />
          )}
          {entry.raw_free_input && (
            <DataRow label="Free Input" value={entry.raw_free_input} c={c} mono={false} />
          )}
        </tbody>
      </table>
    </div>
  )
}

// Same Yes/No the SIBS emails carry in their Unsolicited column.
export function unsolicitedLabel(entry: Pick<SibsEntry, 'unsolicited'>): string | null {
  return entry.unsolicited === true ? 'Yes' : entry.unsolicited === false ? 'No' : null
}

// ─── SibsEmailWidget ──────────────────────────────────────────────────────────

const BODY_PREVIEW_COLUMNS: ColumnDef[] = [
  { key: 'date',         label: 'Date' },
  { key: 'type',         label: 'Type' },
  { key: 'sender',       label: 'Sender' },
  { key: 'scam_content', label: 'Scam Content', thClassName: 'min-w-[180px]' },
  { key: 'org_entity',   label: 'Org Entity' },
  { key: 'reference',    label: 'Reference' },
  { key: 'beneficiario', label: 'Beneficiário' },
  { key: 'platform',     label: 'Platform' },
  { key: 'message_type', label: 'Message Type' },
  { key: 'unsolicited',  label: 'Unsolicited' },
  { key: 'email_ref',    label: 'Email Ref' },
]

const LOG_COLUMNS: ColumnDef[] = [
  { key: 'date',    label: 'Date' },
  { key: 'to',      label: 'To' },
  { key: 'bcc',     label: 'BCC' },
  { key: 'subject', label: 'Subject' },
  { key: 'ref',     label: 'Ref' },
  { key: 'status',  label: 'Status' },
]

const ENTITY_REF_EMAIL_COLUMNS: ColumnDef[] = [
  { key: 'sent_at',       label: 'Sent At' },
  { key: 'entry',         label: 'Entry' },
  { key: 'beneficiario',  label: 'Beneficiário' },
  { key: 'to',            label: 'To' },
  { key: 'subject',       label: 'Subject' },
  { key: 'ref',           label: 'Ref' },
  { key: 'status',        label: 'Status' },
]

export function SibsEmailWidget({
  entry,
  theme,
  sentIds,
  onSent,
}: {
  entry: SibsEntry
  theme: ProcessedTheme
  sentIds: Set<string>
  onSent: (id: string) => void
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'

  const defaultSubject = React.useMemo(() => {
    const dateStr = parseDbTimestamp(entry.created_at)?.toLocaleDateString('pt-PT') ?? '—'
    return `SIBS Fraud Alert · ${sibsDispatchSubjectScope(entry)} · ${dateStr}`
  }, [entry])

  const [recipients, setRecipients] = React.useState<string[]>(['helpdesk.fraud@sibs.com'])
  const [bcc, setBcc]               = React.useState<string[]>([process.env.NEXT_PUBLIC_ALERTS_BCC_EMAIL ?? 'alerts@mail.trustfence.ai'])
  const [subject, setSubject]       = React.useState(defaultSubject)
  const [logId, setLogId] = React.useState(newLogId)
  const [includeMockup, setIncludeMockup] = React.useState(false)
  const [showAttachmentPreview, setShowAttachmentPreview] = React.useState(false)
  const [dispatching, setDispatching] = React.useState(false)
  const [result, setResult]           = React.useState<{ ok: boolean; msg: string } | null>(null)
  const [logs, setLogs]               = React.useState<any[]>([])
  const [logsLoading, setLogsLoading] = React.useState(false)
  const [logsError, setLogsError]     = React.useState<string | null>(null)
  const logsAutoRetryCountRef = React.useRef(0)
  const logsRetryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const comm = React.useMemo(() => sibsCommunication(entry), [entry])
  const finalSubject = `${subject} [REF-${logId}]`

  React.useEffect(() => {
    setSubject(defaultSubject)
    setLogId(newLogId())
    setResult(null)
    setIncludeMockup(false)
    setShowAttachmentPreview(false)
    logsAutoRetryCountRef.current = 0
    if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current)
    fetchLogs()
    return () => { if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, defaultSubject])

  // showLoading is false for the silent background auto-retries (see
  // handleLogsFailure) so they never flash "Loading…" — only the initial
  // load / an explicit manual Retry click do.
  const doFetchLogs = (showLoading: boolean) => {
    if (showLoading) setLogsLoading(true)
    // userEntryId, when the entry has one, takes priority over entry.id on
    // the backend — see that endpoint's own doc comment for why entry.id
    // (raw_entries.id pre-Accept, processed_entries.id after) drifts across
    // the entry's lifecycle while user_entry_id doesn't.
    fetch(`/api/campaigns/logs/${entry.id}?campaign=sibs_dispatch${entry.user_entry_id ? `&userEntryId=${entry.user_entry_id}` : ''}`)
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success) {
          const data: any[] = payload.data ?? []
          setLogs(data)
          setLogsError(null)
          logsAutoRetryCountRef.current = 0
          if (data.length > 0) onSent(entry.id)
        } else {
          handleLogsFailure(payload?.error ?? 'Failed to load email log')
        }
      })
      .catch(() => handleLogsFailure('Network error loading email log'))
      .finally(() => { if (showLoading) setLogsLoading(false) })
  }

  const fetchLogs = () => doFetchLogs(true)

  const handleLogsFailure = (message: string) => {
    if (logsAutoRetryCountRef.current < LOGS_SILENT_MAX_AUTO_RETRIES) {
      logsAutoRetryCountRef.current += 1
      logsRetryTimerRef.current = setTimeout(() => doFetchLogs(false), logsSilentRetryDelayMs())
    } else {
      setLogsError(message)
    }
  }

  const dispatch = async () => {
    if (dispatching) return
    setDispatching(true)
    setResult(null)
    try {
      const res = await fetch('/api/campaigns/sibs', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ entry, subject: finalSubject, logId, to: recipients.join(','), bcc: bcc.length > 0 ? bcc.join(',') : undefined, includeMockup }),
      })
      const payload = await res.json().catch(() => null)
      if (payload?.success) {
        setResult({ ok: true, msg: 'Alert dispatched successfully.' })
        setLogId(newLogId())
        onSent(entry.id)
        fetchLogs()
      } else {
        setResult({ ok: false, msg: payload?.error ?? 'Dispatch failed.' })
      }
    } catch {
      setResult({ ok: false, msg: 'Network error.' })
    } finally {
      setDispatching(false)
    }
  }

  const dispatched = sentIds.has(entry.id)

  // Once an entry has already been dispatched, the form (and — critically —
  // the Dispatch button itself) collapses behind an explicit toggle instead
  // of sitting there ready to click. Applied once, the first time we see
  // `dispatched` go true (covers both "already sent when this widget
  // mounted" and "just sent it"), not on every render, so a deliberate
  // re-expand isn't immediately stomped back closed.
  const [formCollapsed, setFormCollapsed] = React.useState(false)
  const appliedDispatchedDefaultRef = React.useRef(false)
  React.useEffect(() => {
    if (dispatched && !appliedDispatchedDefaultRef.current) {
      appliedDispatchedDefaultRef.current = true
      setFormCollapsed(true)
    }
  }, [dispatched])

  return (
    <div className="flex flex-col gap-2">
      {/* Status + action row */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-1.5 font-mono text-[10px] ${dispatched ? 'text-signal' : c.faint}`}>
          <Mail className="h-3 w-3 shrink-0" />
          {dispatched ? 'Previously dispatched' : 'Not dispatched yet'}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {dispatched && (
            <button
              type="button"
              onClick={() => setFormCollapsed((v) => !v)}
              title={formCollapsed ? 'Show dispatch form' : 'Hide dispatch form'}
              className={`flex h-6 w-6 items-center justify-center rounded border transition-colors hover:text-signal border-border text-muted-foreground`}
            >
              {formCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
          {!formCollapsed && (
            <>
              <Button
                variant="outline"
                size="sm"
                className={`h-6 px-2 font-mono text-[10px] uppercase tracking-widest ${''}`}
                onClick={() => { setSubject(defaultSubject); setResult(null) }}
              >
                Reset
              </Button>
              <Button
                size="sm"
                className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest gap-1"
                disabled={dispatching}
                onClick={dispatch}
              >
                <Send className="h-2.5 w-2.5" />
                {dispatching ? 'Sending…' : 'Dispatch'}
              </Button>
            </>
          )}
        </div>
      </div>

      {!formCollapsed && (
      <>
      {/* Inline form */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_2fr]">
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>To</span>
          <EmailPillInput value={recipients} onChange={setRecipients} theme={theme} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>BCC</span>
          <EmailPillInput value={bcc} onChange={setBcc} theme={theme} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Subject</span>
          <div className="flex items-center gap-1.5">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={`h-7 flex-1 min-w-0 text-[11px] px-2 ${''}`}
            />
            {/* The [REF-<uuid>] tag is appended automatically and can't be edited
                out — it IS this dispatch's email_logs.id, and is how a reply
                gets matched back to it (see routes/cloudflareWorkers/email.ts). */}
            <span
              title={`Sent as: ${finalSubject}`}
              className={`shrink-0 rounded border px-1.5 py-1 font-mono text-[10px] font-semibold ${
                'border-signal/30 bg-signal-surface text-signal'
              }`}
            >
              REF-{logId.slice(0, 8)}…
            </span>
          </div>
        </label>
      </div>

      {/* Message mockup attachment — opt-in and collapsed by default so
          this compact form doesn't have to make room for the thumbnail
          preview unless someone actually wants to attach it. */}
      {comm && (
        <div>
          <button
            type="button"
            onClick={() => setShowAttachmentPreview((v) => !v)}
            className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${includeMockup ? 'text-signal' : c.faint}`}
          >
            <Paperclip className="h-3 w-3" />
            Attachments{includeMockup ? ' · 1' : ''}
            <ChevronDown className={`h-3 w-3 transition-transform ${showAttachmentPreview ? 'rotate-180' : ''}`} />
          </button>
          {showAttachmentPreview && (
            <div className="mt-1.5">
              <MockupToggleCard
                communication={comm}
                included={includeMockup}
                onToggle={() => setIncludeMockup((v) => !v)}
                theme={theme}
              />
            </div>
          )}
        </div>
      )}

      {/* Body preview */}
      <div>
        <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${c.faint}`}>Body preview</div>
        <div className={`overflow-x-auto border rounded ${c.line}`}>
          <ResizableTable
            columns={BODY_PREVIEW_COLUMNS}
            tableClassName="w-full text-[11px] border-collapse"
            thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest bg-secondary border-border text-muted-foreground`}
            thPadding="px-2 py-1"
          >
            <tr className={'bg-card'}>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{fmtDateShort(entry.created_at)}</TableTd>
              <TableTd dense className={`font-semibold whitespace-nowrap ${entry.entry_type === 'ENTITY_REFERENCE' ? 'text-destructive' : 'text-signal'}`}>{entry.entry_type ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.sender ?? '—'}</TableTd>
              <TableTd dense className={`max-w-[180px] ${c.faint}`}>
                <span className="whitespace-pre-wrap break-words">{entry.message_content}</span>
              </TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.entity_reference_entity ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.entity_reference_reference ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.beneficiario ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{entry.platform_text ?? entry.platform_namelong ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.message_type_text?.trim() || '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{unsolicitedLabel(entry) ?? '—'}</TableTd>
              <TableTd dense className="whitespace-nowrap">
                <CopyChip value={logId} display={logId.slice(0, 8)} />
              </TableTd>
            </tr>
          </ResizableTable>
        </div>
      </div>

      {result && (
        <div className={`font-mono text-[10px] px-2 py-1 border rounded ${
          result.ok
            ? 'bg-success-surface border-success/40 text-success'
            : 'bg-danger-surface border-destructive/40 text-destructive'
        }`}>
          {result.msg}
        </div>
      )}
      </>
      )}

      {/* Log */}
      <div className={`border-t pt-2 mt-1 ${c.line}`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Email log</span>
          <button onClick={fetchLogs} className={`font-mono text-[9px] uppercase tracking-widest hover:text-signal ${c.faint}`}>
            refresh
          </button>
        </div>
        {logsLoading ? (
          <p className={`font-mono text-[10px] animate-pulse uppercase tracking-widest ${c.faint}`}>Loading…</p>
        ) : logsError ? (
          <RetryBanner message={logsError} onRetry={fetchLogs} retrying={logsLoading} isDark={isDark} />
        ) : logs.length === 0 ? (
          <p className={`font-mono text-[10px] uppercase tracking-widest ${dispatched ? 'text-warning' : c.faint}`}>
            {dispatched
              ? 'Marked as dispatched, but no matching log record was found — try refresh'
              : 'No emails sent yet'}
          </p>
        ) : (
          <div className={`overflow-x-auto border ${c.line} rounded`}>
            <ResizableTable
              columns={LOG_COLUMNS}
              tableClassName="w-full text-[11px] border-collapse"
              thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest bg-secondary border-border text-muted-foreground`}
              thPadding="px-2 py-1"
            >
              {logs.map((log) => (
                <tr key={log.id} className={`border-b last:border-0 ${c.line}`}>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{fmtDateShort(log.created_at)}</TableTd>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{(log.to ?? []).join(', ')}</TableTd>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{(log.bcc ?? []).join(', ') || '—'}</TableTd>
                  <TableTd dense className={`max-w-[200px] truncate ${c.muted}`}>{log.subject}</TableTd>
                  <TableTd dense className="whitespace-nowrap">
                    {log.id ? <CopyChip value={log.id} display={log.id.slice(0, 8)} /> : '—'}
                  </TableTd>
                  <TableTd dense className="whitespace-nowrap">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded ${
                      log.status === 'sent'
                        ? 'bg-success-surface border-success/40 text-success'
                        : 'bg-danger-surface border-destructive/40 text-destructive'
                    }`}>
                      {log.status}
                    </span>
                  </TableTd>
                </tr>
              ))}
            </ResizableTable>
          </div>
        )}
      </div>
    </div>
  )
}

export function SibsEntityReferenceEmailsWidget({ entry, theme }: { entry: SibsEntry; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const [logs, setLogs] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const logsAutoRetryCountRef = React.useRef(0)
  const logsRetryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // showLoading is false for the silent background auto-retries (see
  // handleLogsFailure) so they never flash "Loading…" — only the initial
  // load / an explicit manual Retry click do.
  const doFetchLogs = React.useCallback((showLoading: boolean) => {
    if (!entry.entity_reference_id) {
      setLogs([])
      setError(null)
      return
    }
    if (showLoading) setLoading(true)
    const params = new URLSearchParams({ entityReferenceId: entry.entity_reference_id })
    if (entry.user_entry_id) params.set('excludeUserEntryId', entry.user_entry_id)
    fetch(`/api/campaigns/sibs/entity-reference-emails?${params.toString()}`)
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success) {
          setLogs(payload.data ?? [])
          setError(null)
          logsAutoRetryCountRef.current = 0
        } else {
          handleLogsFailure(payload?.error ?? 'Failed to load previous emails')
        }
      })
      .catch(() => handleLogsFailure('Network error'))
      .finally(() => { if (showLoading) setLoading(false) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.entity_reference_id, entry.user_entry_id])

  const handleLogsFailure = (message: string) => {
    if (logsAutoRetryCountRef.current < LOGS_SILENT_MAX_AUTO_RETRIES) {
      logsAutoRetryCountRef.current += 1
      logsRetryTimerRef.current = setTimeout(() => doFetchLogs(false), logsSilentRetryDelayMs())
    } else {
      setError(message)
    }
  }

  const fetchLogs = React.useCallback(() => doFetchLogs(true), [doFetchLogs])

  React.useEffect(() => {
    logsAutoRetryCountRef.current = 0
    if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current)
    fetchLogs()
    return () => { if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current) }
  }, [fetchLogs])

  if (!entry.entity_reference_id) {
    return <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No entity reference id on this entry</p>
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
          Same entity reference · excluding current user entry
        </div>
        <button onClick={fetchLogs} className={`font-mono text-[9px] uppercase tracking-widest hover:text-signal ${c.faint}`}>
          refresh
        </button>
      </div>

      {loading ? (
        <p className={`font-mono text-[10px] animate-pulse uppercase tracking-widest ${c.faint}`}>Loading…</p>
      ) : error ? (
        <RetryBanner message={error} onRetry={fetchLogs} retrying={loading} isDark={isDark} />
      ) : logs.length === 0 ? (
        <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No previous emails for this entity reference</p>
      ) : (
        <div className={`overflow-x-auto border ${c.line} rounded`}>
          <ResizableTable
            columns={ENTITY_REF_EMAIL_COLUMNS}
            tableClassName="w-full text-[11px] border-collapse"
            thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest bg-secondary border-border text-muted-foreground`}
            thPadding="px-2 py-1"
          >
            {logs.map((log) => (
              <tr key={log.id} className={`border-b last:border-0 ${c.line}`}>
                <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{fmtDateShort(log.created_at)}</TableTd>
                <TableTd dense className="whitespace-nowrap">
                  {log.entry?.user_entry_id ? <CopyChip value={log.entry.user_entry_id} display={log.entry.user_entry_id.slice(0, 8)} /> : '—'}
                </TableTd>
                <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{log.entry?.beneficiario ?? '—'}</TableTd>
                <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{(log.to ?? []).join(', ')}</TableTd>
                <TableTd dense className={`max-w-[240px] truncate ${c.muted}`}>{log.subject}</TableTd>
                <TableTd dense className="whitespace-nowrap">
                  {log.id ? <CopyChip value={log.id} display={log.id.slice(0, 8)} /> : '—'}
                </TableTd>
                <TableTd dense className="whitespace-nowrap">
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded ${
                    log.status === 'sent'
                      ? 'bg-success-surface border-success/40 text-success'
                      : 'bg-danger-surface border-destructive/40 text-destructive'
                  }`}>
                    {log.status}
                  </span>
                </TableTd>
              </tr>
            ))}
          </ResizableTable>
        </div>
      )}
    </div>
  )
}

// ─── SibsCaseReportWidget ─────────────────────────────────────────────────────

const CASE_LOG_COLUMNS: ColumnDef[] = [
  { key: 'date',    label: 'Date' },
  { key: 'to',      label: 'To' },
  { key: 'bcc',     label: 'BCC' },
  { key: 'subject', label: 'Subject' },
  { key: 'ref',     label: 'Ref' },
  { key: 'status',  label: 'Status' },
]

export function SibsCaseReportWidget({ entry, theme }: { entry: SibsEntry; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'

  const defaultSubject = React.useMemo(() => {
    const dateStr = parseDbTimestamp(entry.created_at)?.toLocaleDateString('pt-PT') ?? '—'
    return `New Fraud Type · Validation Request · ${dateStr}`
  }, [entry])

  const [recipients, setRecipients] = React.useState<string[]>(['helpdesk.fraud@sibs.com'])
  const [bcc, setBcc]               = React.useState<string[]>([process.env.NEXT_PUBLIC_ALERTS_BCC_EMAIL ?? 'alerts@mail.trustfence.ai'])
  const [subject, setSubject]       = React.useState(defaultSubject)
  const [logId, setLogId] = React.useState(newLogId)
  const [description, setDescription] = React.useState('')
  const [includeMockup, setIncludeMockup] = React.useState(true)
  const [dispatching, setDispatching] = React.useState(false)
  const [result, setResult]           = React.useState<{ ok: boolean; msg: string } | null>(null)
  const [logs, setLogs]               = React.useState<any[]>([])
  const [logsLoading, setLogsLoading] = React.useState(false)
  const [logsError, setLogsError]     = React.useState<string | null>(null)
  const logsAutoRetryCountRef = React.useRef(0)
  const logsRetryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const comm = React.useMemo(() => sibsCommunication(entry), [entry])
  const finalSubject = `${subject} [REF-${logId}]`

  React.useEffect(() => {
    setSubject(defaultSubject)
    setLogId(newLogId())
    setResult(null)
    setDescription('')
    setIncludeMockup(true)
    logsAutoRetryCountRef.current = 0
    if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current)
    fetchLogs()
    return () => { if (logsRetryTimerRef.current) clearTimeout(logsRetryTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, defaultSubject])

  // showLoading is false for the silent background auto-retries (see
  // handleLogsFailure) so they never flash "Loading…" — only the initial
  // load / an explicit manual Retry click do.
  const doFetchLogs = (showLoading: boolean) => {
    if (showLoading) setLogsLoading(true)
    // userEntryId, when the entry has one, takes priority over entry.id on
    // the backend — same reasoning as SibsEmailWidget's own fetchLogs above.
    fetch(`/api/campaigns/logs/${entry.id}?campaign=sibs_case_report${entry.user_entry_id ? `&userEntryId=${entry.user_entry_id}` : ''}`)
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success) {
          setLogs(payload.data ?? [])
          setLogsError(null)
          logsAutoRetryCountRef.current = 0
        } else {
          handleLogsFailure(payload?.error ?? 'Failed to load case report log')
        }
      })
      .catch(() => handleLogsFailure('Network error loading case report log'))
      .finally(() => { if (showLoading) setLogsLoading(false) })
  }

  const fetchLogs = () => doFetchLogs(true)

  const handleLogsFailure = (message: string) => {
    if (logsAutoRetryCountRef.current < LOGS_SILENT_MAX_AUTO_RETRIES) {
      logsAutoRetryCountRef.current += 1
      logsRetryTimerRef.current = setTimeout(() => doFetchLogs(false), logsSilentRetryDelayMs())
    } else {
      setLogsError(message)
    }
  }

  const dispatch = async () => {
    if (dispatching || !description.trim()) return
    setDispatching(true)
    setResult(null)
    try {
      const res = await fetch('/api/campaigns/sibs/report-case', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ entry, description, subject: finalSubject, logId, to: recipients.join(','), bcc: bcc.length > 0 ? bcc.join(',') : undefined, includeMockup }),
      })
      const payload = await res.json().catch(() => null)
      if (payload?.success) {
        setResult({ ok: true, msg: 'Sent to SIBS for validation.' })
        setLogId(newLogId())
        fetchLogs()
      } else {
        setResult({ ok: false, msg: payload?.error ?? 'Send failed.' })
      }
    } catch {
      setResult({ ok: false, msg: 'Network error.' })
    } finally {
      setDispatching(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Status + action row */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-1.5 font-mono text-[10px] ${c.faint}`}>
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Describe a new fraud type and ask the SIBS helpdesk to validate it
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            size="sm"
            className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest gap-1"
            disabled={dispatching || !description.trim()}
            onClick={dispatch}
          >
            <Send className="h-2.5 w-2.5" />
            {dispatching ? 'Sending…' : 'Send for validation'}
          </Button>
        </div>
      </div>

      {/* Inline form */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_2fr]">
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>To</span>
          <EmailPillInput value={recipients} onChange={setRecipients} theme={theme} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>BCC</span>
          <EmailPillInput value={bcc} onChange={setBcc} theme={theme} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Subject</span>
          <div className="flex items-center gap-1.5">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={`h-7 flex-1 min-w-0 text-[11px] px-2 ${''}`}
            />
            {/* The [REF-<uuid>] tag is appended automatically and can't be edited
                out — it IS this dispatch's email_logs.id, and is how a reply
                gets matched back to it (see routes/cloudflareWorkers/email.ts). */}
            <span
              title={`Sent as: ${finalSubject}`}
              className={`shrink-0 rounded border px-1.5 py-1 font-mono text-[10px] font-semibold ${
                'border-signal/30 bg-signal-surface text-signal'
              }`}
            >
              REF-{logId.slice(0, 8)}…
            </span>
          </div>
        </label>
      </div>

      {/* Fraud description — this is sent verbatim as the email's opening
          paragraph (see /campaigns/sibs/report-case), not just a table cell,
          so what's typed here is exactly what SIBS reads first. */}
      <label className="flex flex-col gap-0.5">
        <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>New fraud type description (sent as the email's opening paragraph)</span>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. We have identified a pattern that may represent a new fraud type, based on the entry below. Could you kindly confirm whether this matches a known typology on SIBS's side, or provide any further information you may have regarding this campaign?"
          rows={3}
          className={`text-[12px] px-2 py-1.5 ${''}`}
        />
      </label>

      {/* Message mockup — included by default, click to remove */}
      {comm && (
        <div>
          <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${c.faint}`}>Attach to email</div>
          <MockupToggleCard
            communication={comm}
            included={includeMockup}
            onToggle={() => setIncludeMockup((v) => !v)}
            theme={theme}
          />
        </div>
      )}

      {/* Report preview — same table shape as the Email Alert widget, with the
          description standing in for this entry's message content */}
      <div>
        <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${c.faint}`}>Report preview</div>
        <div className={`overflow-x-auto border rounded ${c.line}`}>
          <ResizableTable
            columns={BODY_PREVIEW_COLUMNS}
            tableClassName="w-full text-[11px] border-collapse"
            thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest bg-secondary border-border text-muted-foreground`}
            thPadding="px-2 py-1"
          >
            <tr className={'bg-card'}>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{fmtDateShort(entry.created_at)}</TableTd>
              <TableTd dense className="font-semibold whitespace-nowrap text-warning">NEW TYPE</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.sender ?? '—'}</TableTd>
              <TableTd dense className={`max-w-[180px] ${c.faint}`}>
                <span className="whitespace-pre-wrap break-words">{description || '—'}</span>
              </TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.entity_reference_entity ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.entity_reference_reference ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.beneficiario ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{entry.platform_text ?? entry.platform_namelong ?? '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{entry.message_type_text?.trim() || '—'}</TableTd>
              <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{unsolicitedLabel(entry) ?? '—'}</TableTd>
              <TableTd dense className="whitespace-nowrap">
                <CopyChip value={logId} display={logId.slice(0, 8)} />
              </TableTd>
            </tr>
          </ResizableTable>
        </div>
      </div>

      {result && (
        <div className={`font-mono text-[10px] px-2 py-1 border rounded ${
          result.ok
            ? 'bg-success-surface border-success/40 text-success'
            : 'bg-danger-surface border-destructive/40 text-destructive'
        }`}>
          {result.msg}
        </div>
      )}

      {/* Log */}
      <div className={`border-t pt-2 mt-1 ${c.line}`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Case report log</span>
          <button onClick={fetchLogs} className={`font-mono text-[9px] uppercase tracking-widest hover:text-signal ${c.faint}`}>
            refresh
          </button>
        </div>
        {logsLoading ? (
          <p className={`font-mono text-[10px] animate-pulse uppercase tracking-widest ${c.faint}`}>Loading…</p>
        ) : logsError ? (
          <RetryBanner message={logsError} onRetry={fetchLogs} retrying={logsLoading} isDark={isDark} />
        ) : logs.length === 0 ? (
          <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No case reports sent yet</p>
        ) : (
          <div className={`overflow-x-auto border ${c.line} rounded`}>
            <ResizableTable
              columns={CASE_LOG_COLUMNS}
              tableClassName="w-full text-[11px] border-collapse"
              thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest bg-secondary border-border text-muted-foreground`}
              thPadding="px-2 py-1"
            >
              {logs.map((log) => (
                <tr key={log.id} className={`border-b last:border-0 ${c.line}`}>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{fmtDateShort(log.created_at)}</TableTd>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.muted}`}>{(log.to ?? []).join(', ')}</TableTd>
                  <TableTd dense className={`font-mono whitespace-nowrap ${c.faint}`}>{(log.bcc ?? []).join(', ') || '—'}</TableTd>
                  <TableTd dense className={`max-w-[200px] truncate ${c.muted}`}>{log.subject}</TableTd>
                  <TableTd dense className="whitespace-nowrap">
                    {log.id ? <CopyChip value={log.id} display={log.id.slice(0, 8)} /> : '—'}
                  </TableTd>
                  <TableTd dense className="whitespace-nowrap">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded ${
                      log.status === 'sent'
                        ? 'bg-success-surface border-success/40 text-success'
                        : 'bg-danger-surface border-destructive/40 text-destructive'
                    }`}>
                      {log.status}
                    </span>
                  </TableTd>
                </tr>
              ))}
            </ResizableTable>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── SibsPipelineWidget ───────────────────────────────────────────────────────

export function SibsPipelineWidget({ entry, theme }: { entry: SibsEntry; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const steps = entry.pipeline_steps ?? []

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set())
  const toggle = (i: number) =>
    setExpanded((prev) => { const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next })

  if (steps.length === 0) {
    return <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No pipeline steps recorded</p>
  }

  return (
    <div className="space-y-1">
      {steps.map((s, i) => {
        const open = expanded.has(i)
        const hasJson = s.json_info != null && Object.keys(s.json_info).length > 0
        return (
          <div key={i} className={`rounded border ${c.line} bg-card`}>
            <button
              className={`flex w-full items-center gap-2 px-3 py-2 text-left ${hasJson ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => hasJson && toggle(i)}
              disabled={!hasJson}
            >
              <span className={`shrink-0 font-mono text-[10px] tabular-nums w-5 text-right ${c.faint}`}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className={`flex-1 font-mono text-[11px] ${c.text}`}>{s.step}</span>
              {s.created_at && (
                <span className={`shrink-0 font-mono text-[9px] ${c.faint}`}>
                  {(() => {
                    const d = parseDbTimestamp(s.created_at)
                    return d ? new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(d) : '—'
                  })()}
                </span>
              )}
              {hasJson && (
                open
                  ? <ChevronDown className={`shrink-0 h-3 w-3 ${c.faint}`} />
                  : <ChevronRight className={`shrink-0 h-3 w-3 ${c.faint}`} />
              )}
            </button>
            {open && hasJson && (
              <div className={`border-t px-3 py-2 ${c.line}`}>
                <pre className={`whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed ${c.text}`}>
                  {JSON.stringify(s.json_info, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── sibsCommunication ────────────────────────────────────────────────────────

export function sibsCommunication(entry: SibsEntry): Communication | undefined {
  if (!entry.message_content) return undefined
  const parsedCreatedAt = parseDbTimestamp(entry.created_at)
  const receivedAt = parsedCreatedAt
    ? new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'short' }).format(parsedCreatedAt)
    : '—'
  const sender = entry.sender ?? entry.entity_reference_entity ?? entry.platform_namelong ?? 'Unknown'
  return { type: 'sms', sender, receivedAt, body: entry.message_content }
}
